/**
 * Tasks store - manages task state with optimistic updates, IndexedDB cache,
 * offline sync queue, and conflict-aware remote change handling.
 */

import { create } from 'zustand';
import type { Task, CreateTaskInput, UpdateTaskInput } from '@anna-calendar/shared';
import * as api from '../api/client';
import { cacheTasks, getCachedTasks, cacheTask, removeCachedTask, enqueueMutation } from '../db/indexeddb';
import { useAuthStore } from './auth';
import { useAppStore } from './app';
import { useSyncStore } from './sync';
import { useConflictsStore } from './conflicts';
import { getQueuedMutations } from '../db/indexeddb';

interface TaskFetchParams {
  due_date?: string;
  due_date_start?: string;
  due_date_end?: string;
  priority?: number;
}

interface TasksState {
  tasks: Task[];
  isLoading: boolean;
  error: string | null;

  fetchTasks: (params?: TaskFetchParams) => Promise<void>;
  createTask: (data: CreateTaskInput) => Promise<Task | null>;
  updateTask: (id: string, data: UpdateTaskInput) => Promise<Task | null>;
  deleteTask: (id: string) => Promise<boolean>;
  applyRemoteChange: (action: string, task: Task | { id: string }) => void;
  loadFromCache: (params?: TaskFetchParams) => Promise<void>;
}

export const useTasksStore = create<TasksState>((set, get) => ({
  tasks: [],
  isLoading: false,
  error: null,

  fetchTasks: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const tasks = await api.fetchWithAuthRetry(
        () => useAuthStore.getState().token,
        (token) => api.fetchTasks(token, params),
      );
      set({ tasks, isLoading: false });
      await cacheTasks(tasks);
    } catch (err: unknown) {
      if (err instanceof api.RefreshFailedError) {
        useAuthStore.getState().logout();
        set({ isLoading: false, error: 'Session expired' });
        return;
      }
      if (err instanceof api.ApiError && err.status === 401) {
        set({ isLoading: false, error: 'Session expired' });
        return;
      }
      const cached = await getCachedTasks(params);
      set({ tasks: cached, isLoading: false, error: null });
    }
  },

  createTask: async (data) => {
    const tempId = crypto.randomUUID();
    const optimistic: Task = {
      ...data,
      id: tempId,
      user_id: '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    set((s) => ({ tasks: [...s.tasks, optimistic] }));
    await cacheTask(optimistic);

    try {
      const created = await api.fetchWithAuthRetry(
        () => useAuthStore.getState().token,
        (token) => api.createTask(token, data),
      );
      set((s) => ({
        tasks: s.tasks.map((t) => (t.id === tempId ? created : t)),
      }));
      await removeCachedTask(tempId);
      await cacheTask(created);
      return created;
    } catch (err) {
      if (err instanceof api.RefreshFailedError) {
        useAuthStore.getState().logout();
        set((s) => ({ tasks: s.tasks.filter((t) => t.id !== tempId) }));
        await removeCachedTask(tempId);
        return null;
      }
      // Always queue failed mutations for retry (API may be temporarily down)
      await enqueueMutation('POST', '/api/tasks', { ...data, id: tempId });
      const q = await getQueuedMutations();
      useSyncStore.getState().setPendingCount(q.length);
      set({ error: 'Task saved locally. Will sync when connected.' });
      return optimistic;
    }
  },

  updateTask: async (id, data) => {
    const prev = get().tasks.find((t) => t.id === id);
    set((s) => ({
      tasks: s.tasks.map((t) =>
        t.id === id ? { ...t, ...data, updated_at: new Date().toISOString() } : t
      ),
    }));

    const conflicts = useConflictsStore.getState();
    conflicts.addPending(id);

    try {
      const updated = await api.fetchWithAuthRetry(
        () => useAuthStore.getState().token,
        (token) => api.updateTask(token, id, data),
      );

      const current = get().tasks.find((t) => t.id === id);
      if (!current || updated.updated_at >= current.updated_at) {
        set((s) => ({
          tasks: s.tasks.map((t) => (t.id === id ? updated : t)),
        }));
        await cacheTask(updated);
      }

      processDeferredChange(id);
      conflicts.removePending(id);
      return updated;
    } catch (err) {
      conflicts.removePending(id);
      if (err instanceof api.RefreshFailedError) {
        useAuthStore.getState().logout();
        if (prev) {
          set((s) => ({ tasks: s.tasks.map((t) => (t.id === id ? prev : t)) }));
        }
        processDeferredChange(id);
        return null;
      }
      // Always queue failed mutations for retry
      await enqueueMutation('PUT', `/api/tasks/${id}`, data);
      const q = await getQueuedMutations();
      useSyncStore.getState().setPendingCount(q.length);
      processDeferredChange(id);
      return prev ? { ...prev, ...data } : null;
    }
  },

  deleteTask: async (id) => {
    const prev = get().tasks.find((t) => t.id === id);
    set((s) => ({ tasks: s.tasks.filter((t) => t.id !== id) }));

    const conflicts = useConflictsStore.getState();
    conflicts.addPending(id);

    try {
      await api.fetchWithAuthRetry(
        () => useAuthStore.getState().token,
        (token) => api.deleteTask(token, id),
      );
      await removeCachedTask(id);
      conflicts.removePending(id);
      return true;
    } catch (err) {
      conflicts.removePending(id);
      if (err instanceof api.RefreshFailedError) {
        useAuthStore.getState().logout();
        if (prev) set((s) => ({ tasks: [...s.tasks, prev] }));
        return false;
      }
      // Always queue failed mutations for retry
      await enqueueMutation('DELETE', `/api/tasks/${id}`, null);
      const q = await getQueuedMutations();
      useSyncStore.getState().setPendingCount(q.length);
      await removeCachedTask(id);
      return true;
    }
  },

  applyRemoteChange: (action, taskOrId) => {
    const conflicts = useConflictsStore.getState();

    if (action === 'create' || action === 'update') {
      const task = taskOrId as Task;

      if (conflicts.isPending(task.id)) {
        conflicts.deferChange(task.id, {
          entity: 'task',
          action,
          data: task as unknown as Record<string, unknown>,
          timestamp: task.updated_at,
        });
        return;
      }

      set((s) => {
        const idx = s.tasks.findIndex((t) => t.id === task.id);
        if (idx >= 0) {
          const local = s.tasks[idx];
          if (local.updated_at > task.updated_at) return s;
          if (local.title !== task.title || local.completed !== task.completed ||
              local.priority !== task.priority || local.due_date !== task.due_date) {
            conflicts.addConflict({
              entityType: 'task',
              entityTitle: task.title,
            });
          }
          const next = [...s.tasks];
          next[idx] = task;
          return { tasks: next };
        }
        return { tasks: [...s.tasks, task] };
      });
      cacheTask(task);
    } else if (action === 'delete') {
      const { id } = taskOrId as { id: string };

      if (conflicts.isPending(id)) {
        conflicts.deferChange(id, {
          entity: 'task',
          action: 'delete',
          data: { id } as Record<string, unknown>,
          timestamp: new Date().toISOString(),
        });
        return;
      }

      set((s) => ({ tasks: s.tasks.filter((t) => t.id !== id) }));
      removeCachedTask(id);
    }
  },

  loadFromCache: async (params) => {
    const cached = await getCachedTasks(params);
    set({ tasks: cached });
  },
}));

function processDeferredChange(entityId: string): void {
  const conflicts = useConflictsStore.getState();
  const deferred = conflicts.popDeferred(entityId);
  if (!deferred) return;

  if (deferred.action === 'delete') {
    useTasksStore.getState().applyRemoteChange('delete', { id: entityId });
  } else {
    const remoteData = deferred.data as unknown as Task;
    const local = useTasksStore.getState().tasks.find((t) => t.id === entityId);
    if (!local || remoteData.updated_at >= local.updated_at) {
      if (local && (local.title !== remoteData.title || local.completed !== remoteData.completed ||
          local.priority !== remoteData.priority)) {
        conflicts.addConflict({
          entityType: 'task',
          entityTitle: remoteData.title,
        });
      }
      useTasksStore.getState().applyRemoteChange(deferred.action, remoteData);
    }
  }
}
